// A person's corrections in the Centro de Control — BR-31's frontend half.
//
// Pure, and here rather than inside a 2,000-line component for the reason `cierreDocuments.js`
// gives about itself: what breaks silently is worth testing. Three things do.
//
//   1. **Which pipeline the open row came from.** BR-23's projection emits `closing_id` and no
//      `file_group_id`; a legacy Mongo register is the other way round, and both reach the
//      component through the same prop. Editing a cierre row through the legacy path posts to a
//      mycelium endpoint keyed on the PRODUCTION Brinks business id, which for a cierre row is
//      wrong in every environment and silent in all but one.
//   2. **A cierre alert is not a PoC alert.** The panel renders `{type, message}` with three
//      values; `closing_alerts` has four severities and three statuses, and a resolved alert has
//      to stay visible — `closing_alerts` is append-only precisely so that "flagged at 09:12,
//      closed by a person at 09:40" survives.
//   3. **What the backend refused, in words.** These four routes answer 400/403/404/409 with a
//      message written for the operator. Swallowing it into "Error al actualizar el campo" throws
//      away the only thing that tells them whether to retype the value, reopen the closing, or
//      call somebody.

import { SIN_PROCESAR_DIA, sinPermiso } from "./accesoDiccionario";

// Which pipeline a Centro de Control row came from.
//
// Asked of the ROW, not of the business: dev's Brinks tenant is both the cierres client and the
// demo fixture's business, so a check on the business id is right in one environment and wrong in
// the other. `getBrinksOfficeDocuments` has branched this way since BR-30.
export const isCierreRegister = (register) => Boolean(register?.closing_id);

// `closing_alerts.severity` -> the three values the alerts pane renders.
//
// `blocked` joins `alert` rather than getting a fourth bucket: it means a rule could not be
// evaluated because an input was missing, which is at least as urgent as one that evaluated and
// disagreed. `info` is the one that must NOT be dressed up — the reconciliation profile raises
// "Cuadre $0" as `info` on a perfect day, and a panel that painted those as critical would make
// every healthy closing look like a problem.
export const SEVERITY_TYPES = {
  alert: "ALERT",
  blocked: "ALERT",
  subalert: "SUBALERT",
  info: "INFO",
  // A difference this closing inherited from an earlier day: shown here, attended on that day.
  carried: "CARRIED",
};

// How an alert was closed, in the operator's words. `RESOLVED_BY_DATA` and `RESOLVED_BY_USER` are
// a genuinely different fact — the figures moved, or a person decided — and the whole reason
// `status` has two resolved values instead of one.
export const STATUS_LABELS = {
  OPEN: "Abierta",
  RESOLVED_BY_DATA: "Resuelta por los datos",
  RESOLVED_BY_USER: "Resuelta por una persona",
};

// One row of `POST /mantisBusiness/taktik/getCierreAlerts`, in the shape the alerts pane renders.
//
// The original fields are kept alongside, because the pane needs more than the PoC's two: whether
// to offer a "Resolver" button at all, and who closed it if somebody already did.
export const toViewerAlert = (alert) => ({
  ...alert,
  type: SEVERITY_TYPES[alert?.severity] || "INFO",
  message: alert?.message || "",
  isOpen: alert?.status === "OPEN",
  statusLabel: STATUS_LABELS[alert?.status] || alert?.status || "",
});

// Open alerts first, then the rest oldest-first.
//
// The backend returns them in `raised_at` order and keeps the resolved ones, which is right for a
// trace and wrong for a work queue: what an operator has to act on should not be below what
// somebody already dealt with.
export const sortAlerts = (alerts = []) =>
  [...alerts].sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    return String(a.raisedAt || "").localeCompare(String(b.raisedAt || ""));
  });

export const GENERIC_ERROR = "No se pudo completar la acción. Intenta de nuevo.";

// BR-38 — a closing outside the person's access is the same 404 as one that does not exist, so
// when access changes while a screen is open the next click on that closing gets it. Said as what
// happened to the operator, not as a failure: the backend's own words are an English sentence
// with a uuid in it, written for a log.
export const CIERRE_NO_DISPONIBLE =
  "Este cierre ya no está disponible. Puede que tu acceso haya cambiado mientras tenías la pantalla abierta: vuelve a cargarla.";

export const NO_DISPONIBLE =
  "Ese cliente u oficina ya no está disponible. Puede que tu acceso haya cambiado mientras tenías la pantalla abierta: vuelve a cargarla.";

// What went wrong, as the backend said it.
//
// `services/cierres/corrections/errors.js` writes each of these for a person to read and act on —
// "reopen it first", "that column is a calculation", "somebody already closed this alert". A
// generic string in their place turns four different, fixable situations into one dead end.
//
// Except the access refusals (BR-38), which are said here: `ACTION_FORBIDDEN` names the permission
// by code, so the sentence comes from the access dictionary, and the 404s and `DOCUMENTS_FORBIDDEN`
// carry English written for a log. Every other refusal is still the backend's own message.
export const correctionError = (response) => {
  switch (response?.errorCode) {
    case "ACTION_FORBIDDEN":
      return response.permission === "cierres.daily.run" ? SIN_PROCESAR_DIA : sinPermiso(response.permission);
    case "DOCUMENTS_FORBIDDEN":
      return sinPermiso("cierres.documents.view");
    case "CLOSING_NOT_FOUND":
      return CIERRE_NO_DISPONIBLE;
    case "NOT_FOUND":
      return NO_DISPONIBLE;
    default:
      return (response && typeof response.message === "string" && response.message.trim()) || GENERIC_ERROR;
  }
};

// Is this panel column something a person may correct?
//
// The answer is the backend's, carried per field by `getExtractionFields` since BR-31. It is not
// inferred here from the group name — group names are per-client configuration, so
// `calculosFields` is Davivienda's word and the next client's may be anything — and it is not
// inferred from the column name, which `panel/normalize.js` is explicit must never be parsed
// apart.
//
// **`undefined` means yes.** The same endpoint serves the PQRS and tangelo panels out of
// `mantisBusinessExtractionFields.js`, which carries no such flag and whose fields have always
// been editable. Defaulting to "not correctable" would silently remove the pencil from every
// non-cierre client. The route is the real gate either way: it answers 400 naming the column.
export const isCorrectableField = (extractionFields, fieldName) => {
  const field = Object.values(extractionFields || {})
    .flatMap((group) => group || [])
    .find((entry) => entry?.fieldName === fieldName);

  if (!field) return false;
  return field.correctable !== false;
};
